import { useEffect } from "react";
import { Header } from "../../components/header/header";
import { Footer } from "../../components/footer/footer";
import "./privacy-policy.scss";

export const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className='privacy-policy'>
            <div className='privacy-policy__header-wrap'>
                <div className='container'>
                    <Header />
                </div>
            </div>
            <div className='container privacy-policy__container'>
                <h1 className='privacy-policy__title'>Privacy Policy</h1>
                <p className='privacy-policy__updated'>Last Updated: January 20, 2026</p>
                <p className='privacy-policy__intro'>
                    Hobby Smart Case LLC ("Company," "we," "our," or "us") operates www.hobbysmartcase.com and the
                    Hobby Smart Case mobile applications for Apple iOS and Google Android, along with the connected
                    smart case hardware (collectively, the "Services").
                </p>
                <p className='privacy-policy__intro'>
                    This Privacy Policy explains how we collect, use, store, and protect your information when you
                    use our website, mobile apps, and smart case devices.
                </p>
                <p className='privacy-policy__intro'>
                    By using the Services, you agree to this Privacy Policy.
                </p>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>1. INFORMATION WE COLLECT</h2>

                    <h3 className='privacy-policy__section-subtitle'>A. Information You Provide</h3>
                    <ul className='privacy-policy__list'>
                        <li>Name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Account credentials</li>
                        <li>Shipping & billing address</li>
                        <li>Order and purchase information</li>
                        <li>Customer support communications</li>
                        <li>Device nicknames and case identifiers</li>
                    </ul>

                    <h3 className='privacy-policy__section-subtitle'>B. Smart Case & App Data</h3>
                    <p>The app and connected device may collect:</p>
                    <ul className='privacy-policy__list'>
                        <li>Case serial number / unique ID</li>
                        <li>Bluetooth connection status</li>
                        <li>Battery level</li>
                        <li>Temperature & humidity readings</li>
                        <li>Lid open/close events</li>
                        <li>Movement alerts</li>
                        <li>Firmware version</li>
                        <li>Multi-case configuration data</li>
                        <li>User preferences</li>
                    </ul>

                    <h3 className='privacy-policy__section-subtitle'>C. Location Data & History</h3>
                    <p>With your permission, we may collect:</p>
                    <ul className='privacy-policy__list'>
                        <li>Real-time GPS location of each Hobby Smart Case</li>
                        <li>Last known location</li>
                        <li>Location history over time for tracking, recovery, and security features</li>
                        <li>Geofence events and alerts</li>
                    </ul>
                    <p>You may disable location access at any time in device settings. Disabling location will limit tracking features.</p>

                    <h3 className='privacy-policy__section-subtitle'>D. Automatically Collected Data</h3>
                    <ul className='privacy-policy__list'>
                        <li>IP address</li>
                        <li>Mobile device identifiers</li>
                        <li>Operating system & app version</li>
                        <li>Crash logs and performance data</li>
                        <li>Cookies and website analytics</li>
                    </ul>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>2. THIRD-PARTY SERVICES WE USE</h2>

                    <h3 className='privacy-policy__section-subtitle'>Firebase (Google)</h3>
                    <p>We use Firebase for:</p>
                    <ul className='privacy-policy__list'>
                        <li>User authentication</li>
                        <li>Push notifications</li>
                        <li>Analytics and usage events</li>
                        <li>Crash reporting</li>
                        <li>Secure database storage</li>
                    </ul>
                    <p>Firebase may process technical identifiers and app usage data in accordance with Google's privacy practices.</p>

                    <h3 className='privacy-policy__section-subtitle'>Onomondo Cellular Connectivity</h3>
                    <p>Hobby Smart Case devices use Onomondo for LTE-M/NB-IoT connectivity. Onomondo may process:</p>
                    <ul className='privacy-policy__list'>
                        <li>SIM identifiers</li>
                        <li>Network connection metadata</li>
                        <li>Data required to transmit GPS and sensor information</li>
                    </ul>
                    <p>This data is used solely to enable device communication.</p>

                    <h3 className='privacy-policy__section-subtitle'>Shopify Payments & Checkout</h3>
                    <p>Purchases on hobbysmartcase.com are processed via Shopify & Shopify Payments.</p>
                    <ul className='privacy-policy__list'>
                        <li>We do not store credit card numbers</li>
                        <li>Payment data is handled by PCI-compliant processors</li>
                        <li>Order history is stored in Shopify systems</li>
                    </ul>

                    <h3 className='privacy-policy__section-subtitle'>Additional Providers</h3>
                    <ul className='privacy-policy__list'>
                        <li>Apple Push Notification Service</li>
                        <li>Google Play Services</li>
                        <li>Cloud hosting partners</li>
                        <li>Email service providers</li>
                    </ul>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>3. HOW WE USE YOUR INFORMATION</h2>
                    <p>We use data to:</p>
                    <ul className='privacy-policy__list'>
                        <li>Provide GPS tracking and alerts</li>
                        <li>Manage multiple Hobby Smart Cases under one account</li>
                        <li>Display location history and timelines</li>
                        <li>Send temperature, movement, and security alerts</li>
                        <li>Improve firmware and app performance</li>
                        <li>Process Shopify orders and warranties</li>
                        <li>Provide customer support</li>
                        <li>Prevent fraud or abuse</li>
                        <li>Meet legal requirements</li>
                    </ul>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>4. MULTIPLE CASE MANAGEMENT</h2>
                    <p>Users may register multiple Hobby Smart Cases within a single account. For each case we may store:</p>
                    <ul className='privacy-policy__list'>
                        <li>Individual device settings</li>
                        <li>Separate location histories</li>
                        <li>Custom names</li>
                        <li>Alert preferences</li>
                        <li>Sensor logs</li>
                    </ul>
                    <p>Data is segmented by device to ensure accurate management.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>5. SHARING OF INFORMATION</h2>
                    <p>We do not sell personal data.</p>
                    <p>We may share data with:</p>
                    <ul className='privacy-policy__list'>
                        <li>Firebase / Google (analytics & infrastructure)</li>
                        <li>Onomondo (cellular connectivity)</li>
                        <li>Shopify (payments & orders)</li>
                        <li>Shipping partners</li>
                        <li>Law enforcement when legally required</li>
                    </ul>
                    <p>All partners are required to protect your data.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>6. DATA RETENTION</h2>
                    <p>We retain:</p>
                    <ul className='privacy-policy__list'>
                        <li>Account data while active</li>
                        <li>Location history to support recovery and analytics</li>
                        <li>Order records for legal requirements</li>
                        <li>Sensor logs to improve product performance</li>
                    </ul>
                    <p>You may request deletion at any time.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>7. YOUR CONTROLS</h2>
                    <p>You can:</p>
                    <ul className='privacy-policy__list'>
                        <li>Disable GPS permissions</li>
                        <li>Turn off Bluetooth</li>
                        <li>Delete individual case history</li>
                        <li>Remove a case from your account</li>
                        <li>Request full account deletion</li>
                        <li>Export your data</li>
                    </ul>
                    <p>Requests: <a href="mailto:privacy@hobbysmartcase.com">privacy@hobbysmartcase.com</a></p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>8. SECURITY</h2>
                    <p>We implement:</p>
                    <ul className='privacy-policy__list'>
                        <li>Encrypted data transmission</li>
                        <li>Secure authentication via Firebase</li>
                        <li>Access controls</li>
                        <li>Regular security updates</li>
                    </ul>
                    <p>No system is 100% secure, but we follow industry standards.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>9. CHILDREN</h2>
                    <p>Services are not intended for children under 13. We do not knowingly collect children's data.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>10. CALIFORNIA (CCPA)</h2>
                    <p>California residents may request:</p>
                    <ul className='privacy-policy__list'>
                        <li>Access to personal data</li>
                        <li>Deletion</li>
                        <li>Disclosure of data use</li>
                    </ul>
                    <p>We do not sell personal information.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>11. GDPR (EU)</h2>
                    <p>You have rights to:</p>
                    <ul className='privacy-policy__list'>
                        <li>Access</li>
                        <li>Rectification</li>
                        <li>Erasure</li>
                        <li>Restrict processing</li>
                        <li>Portability</li>
                        <li>Withdraw consent</li>
                    </ul>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>12. INTERNATIONAL TRANSFERS</h2>
                    <p>Data may be processed in the United States and other regions where our partners operate.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>13. CHANGES</h2>
                    <p>We may update this policy. Continued use means acceptance.</p>
                </section>

                <section className='privacy-policy__section'>
                    <h2 className='privacy-policy__section-title'>14. CONTACT</h2>
                    <p>Hobby Smart Case LLC</p>
                    <p>Website: <a href="https://www.hobbysmartcase.com">https://www.hobbysmartcase.com</a></p>
                    <p>Email: <a href="mailto:privacy@hobbysmartcase.com">privacy@hobbysmartcase.com</a></p>
                </section>
            </div>
            <Footer />
        </div>
    );
};
